import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LayoutGrid, List, FileText, Clock, AlertCircle, Star, Users, CheckCircle2 } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import JobSelect from '../components/candidates/JobSelect';
import CandidatesTable from '../components/candidates/CandidatesTable';
import CandidatesGrid from '../components/candidates/CandidatesGrid';
import AddCandidateButton from '../components/candidates/AddCandidateButton';
import AddCandidateModal from '../components/candidates/AddCandidateModal';
import CandidateReferenceModal from '../components/candidates/CandidateReferenceModal';
import StatCard from '../components/dashboard/StatCard';
import FinalReportModal from '../components/dashboard/CompletedJobs/FinalReportModal';
import CompleteJobButton from '../components/dashboard/CompleteJobButton';
import { jobsApi } from '../api/jobs';
import { candidatesApi, Candidate } from '../api/candidates';
import { useLanguage } from '../contexts/LanguageContext';
import { useDataRefresh } from '../contexts/DataRefreshContext';

const Candidates = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLanguage();
  const { refreshCounters, refreshJobs, refreshCandidates, refreshAll } = useDataRefresh();
  const [selectedJobId, setSelectedJobId] = useState('');
  const [jobStatus, setJobStatus] = useState<'ACTIVE' | 'COMPLETED' | 'ALL'>('ACTIVE');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFinalReport, setShowFinalReport] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [jobs, setJobs] = useState<import('../data/mockJobs').Job[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  
  // Load initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        console.log('Fetching candidates data, triggered by refreshCounters:', 
          refreshCounters.jobs, refreshCounters.candidates, refreshCounters.references);
        // Load jobs
        const jobsData = await jobsApi.getAll();
        
        // Transform API job data to match expected format in components
        const transformedJobs = jobsData.map(job => ({
          id: job.id,
          title: job.job_title,
          department: job.department,
          location: 'Remote', // Default location
          candidates: 0, // Default value
          referencesNeeded: job.required_number_of_references,
          referencesCompleted: 0, // Default value
          status: job.status || 'ACTIVE', // Preserve original status
          priority: 'medium', // Default value
          dueDate: job.application_deadline || new Date().toISOString().split('T')[0]
        }));
        
        setJobs(transformedJobs);
        
        // Load candidates
        const candidatesData = await candidatesApi.getAll();
        setCandidates(candidatesData);
        
        // Update search cache with the latest data
        // Collect all references from candidates
        const allReferences = candidatesData.reduce((refs, candidate) => {
          if (candidate.references && candidate.references.length > 0) {
            return [...refs, ...candidate.references];
          }
          return refs;
        }, [] as any[]);
        
        // Update search cache
        import('../utils/search/searchItems').then(({ updateSearchCache }) => {
          updateSearchCache(jobsData, candidatesData, allReferences);
        });
        
      } catch (error) {
        console.error('Failed to load initial data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialData();
  }, [
    refreshTrigger, 
    refreshCounters.jobs, 
    refreshCounters.candidates, 
    refreshCounters.references
  ]); // Reload data when either local or global refresh triggers change
  
  // Handle URL search params
  useEffect(() => {
    const jobId = searchParams.get('jobId');
    const candidateId = searchParams.get('id');
    const referenceId = searchParams.get('reference');
    const status = searchParams.get('status');
    
    if (jobId) {
      setSelectedJobId(jobId);
    }
    
    // Set job status if provided in URL
    if (status === 'COMPLETED' || status === 'ACTIVE' || status === 'ALL') {
      setJobStatus(status);
    }
    
    // Handle candidate selection from URL
    if (candidateId || referenceId) {
      const loadCandidateDetails = async () => {
        try {
          let candidate: Candidate | undefined;
          
          if (candidateId) {
            // Direct candidate selection
            candidate = candidates.find(c => c.id === candidateId);
            if (!candidate && candidates.length === 0) {
              // If not found in current state and no candidates loaded yet, fetch from API
              candidate = await candidatesApi.getById(candidateId);
            }
          } else if (referenceId) {
            // Find candidate through reference
            candidate = candidates.find(c => 
              c.references?.some(ref => ref.id === referenceId)
            );
            
            if (!candidate && candidates.length === 0) {
              // If not in current state, we need to fetch all candidates to find the match
              // This could be optimized with a dedicated API endpoint in the future
              const allCandidates = await candidatesApi.getAll();
              candidate = allCandidates.find(c => 
                c.references?.some(ref => ref.id === referenceId)
              );
            }
          }
          
          if (candidate) {
            setSelectedCandidate(candidate);
            // Also set the job filter if this candidate belongs to a job
            if (candidate.jobId) {
              setSelectedJobId(candidate.jobId);
            }
          }
        } catch (error) {
          console.error('Failed to load candidate details:', error);
        }
      };
      
      loadCandidateDetails();
    }
  }, [searchParams, candidates]);

  const handleJobChange = (jobId: string) => {
    setSelectedJobId(jobId);
    
    // Identify if the selected job exists and its status
    const selectedJob = jobs.find(j => j.id === jobId);
    const jobStatusValue = selectedJob?.status || 'ACTIVE';
    
    // If the selected job is completed, automatically switch to completed view
    if (jobStatusValue === 'COMPLETED' && jobStatus !== 'COMPLETED') {
      setJobStatus('COMPLETED');
    }
    
    // Update URL search params, preserving other params if needed
    const candidateId = searchParams.get('id');
    const referenceId = searchParams.get('reference');
    
    const newParams: Record<string, string> = {};
    if (jobId) newParams.jobId = jobId;
    if (candidateId) newParams.id = candidateId;
    if (referenceId) newParams.reference = referenceId;
    
    // Use the job's status if available, otherwise use current filter status
    if (jobStatusValue === 'COMPLETED' || jobStatus !== 'ACTIVE') {
      newParams.status = jobStatusValue === 'COMPLETED' ? 'COMPLETED' : jobStatus;
    }
    
    if (Object.keys(newParams).length > 0) {
      setSearchParams(newParams);
    } else {
      setSearchParams({});
    }
  };

  // Filter candidates based on selected job
  const filteredCandidates = selectedJobId
    ? candidates.filter(c => c.jobId === selectedJobId)
    : candidates;

  // Calculate stats based on filtered candidates
  const displayCandidates = selectedJobId ? filteredCandidates : candidates;
  
  // Check if the selected job is completed
  const selectedJob = jobs.find(j => j.id === selectedJobId);
  const isSelectedJobCompleted = selectedJob?.status === 'COMPLETED' || jobStatus === 'COMPLETED';
  
  // Stats calculations based on filtered candidates
  const totalPendingReferencesFiltered = displayCandidates.reduce((total, candidate) => {
    const pendingRefs = candidate.references 
      ? candidate.references.filter(ref => !ref.filled_form_at).length 
      : 0;
    return total + pendingRefs;
  }, 0);
  
  const totalCompletedReferencesFiltered = displayCandidates.reduce((total, candidate) => {
    const completedRefs = candidate.references 
      ? candidate.references.filter(ref => ref.filled_form_at).length 
      : 0;
    return total + completedRefs;
  }, 0);
  
  const totalReferencesFiltered = totalPendingReferencesFiltered + totalCompletedReferencesFiltered;
  const pendingProgressFiltered = totalReferencesFiltered > 0 ? Math.round((totalPendingReferencesFiltered / totalReferencesFiltered) * 100) : 0;
  const completedProgressFiltered = totalReferencesFiltered > 0 ? Math.round((totalCompletedReferencesFiltered / totalReferencesFiltered) * 100) : 0;
  
  // Only count candidates as "incomplete" if they haven't filled their own form yet
  // Ignore reference status - that's tracked separately in the reference stats
  const incompleteApplicationsFiltered = displayCandidates.filter(c => 
    !c.form_filled_at
  ).length;

  // Adapter to transform API candidate data to the format expected by components
  const adaptCandidateForDisplay = (candidate: Candidate) => {
    // Count completed references (those with filled_form_at truthy value)
    const completedRefs = candidate.references ? 
      candidate.references.filter(ref => ref.filled_form_at).length : 0;
    
    // Get total references for this candidate (actual references provided)
    const totalRefs = candidate.references ? candidate.references.length : 0;
    
    console.log('Grid view - Candidate:', candidate.full_name || candidate.email);
    console.log('Grid view - References:', candidate.references);
    console.log('Grid view - Completed:', completedRefs, 'Total:', totalRefs);
    
    return {
      ...candidate,
      id: candidate.id,
      name: candidate.full_name,
      email: candidate.email,
      status: candidate.status,
      jobId: candidate.jobId,
      appliedDate: candidate.created_at,
      // Default avatar image if none provided
      avatar: `https://avatars.dicebear.com/api/initials/${encodeURIComponent(candidate.full_name)}.svg`,
      // Count completed references (using filled_form_at field)
      referencesCompleted: completedRefs,
      // Use actual number of references for this candidate
      referencesRequired: totalRefs,
      // Force the reference count to be correct
      _referenceCount: `${completedRefs}/${totalRefs}`
    };
  };

  const handleCandidateClick = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    // No need to do anything special for isJobCompleted here
    // It's automatically passed to the CandidateReferenceModal below
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 flex flex-col">
        <Header />
        
        <main className="flex-1 p-4 lg:p-6">
          {/* Job Selection Section */}
          <div className="bg-white rounded-xl shadow-sm border p-4 lg:p-6 mb-4 lg:mb-6">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
                <Users className="w-6 h-6 text-blue-600 mr-2" />
                {t('candidates.jobSelect.title')}
              </h2>
              <p className="text-gray-500 text-sm tracking-wide">{t('candidates.jobSelect.instruction')}</p>
            </div>
            <JobSelect
              selectedJobId={selectedJobId}
              onJobChange={handleJobChange}
              jobStatus={jobStatus}
              onStatusChange={(status) => {
                setJobStatus(status);
                // Clear selected job when changing status filter
                setSelectedJobId('');
                // Also clear selected candidate if any
                setSelectedCandidate(null);
                // And ensure the final report modal is closed
                setShowFinalReport(false);
                
                // Update URL search params
                const newParams: Record<string, string> = {};
                
                if (status !== 'ACTIVE') {
                  newParams.status = status;
                }
                
                // Remove jobId from URL when clearing job selection
                setSearchParams(newParams);
              }}
            />
          </div>
          
          {/* Stats Grid - Only shown when a job is selected */}
          {selectedJobId && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-4 lg:mb-6">
              <StatCard
                icon={FileText}
                title={t('candidates.stats.totalCandidates')}
                value={displayCandidates.length}
                progress={100}
                color="bg-blue-500"
                showProgress={displayCandidates.length > 0}
              />
              <StatCard
                icon={AlertCircle}
                title={t('candidates.stats.incompleteApplications')}
                value={incompleteApplicationsFiltered}
                progress={40}
                color="bg-red-500"
                showProgress={incompleteApplicationsFiltered > 0}
              />
              <StatCard
                icon={Clock}
                title={t('candidates.stats.pendingReferences')}
                value={totalPendingReferencesFiltered}
                progress={pendingProgressFiltered}
                color="bg-orange-500"
                showProgress={totalPendingReferencesFiltered > 0}
              />
              <StatCard
                icon={Star}
                title={t('candidates.stats.referencesCompleted')}
                value={totalCompletedReferencesFiltered}
                progress={completedProgressFiltered}
                color="bg-green-500"
                showProgress={totalCompletedReferencesFiltered > 0}
              />
            </div>
          )}
          
          {/* Candidates Header Section */}
          <div className="bg-white rounded-xl shadow-sm border p-4 lg:p-6 mb-4 lg:mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              {selectedJobId ? (
                <div>
                  <div className={`flex flex-col border-l-4 ${isSelectedJobCompleted ? 'border-purple-500' : 'border-blue-500'} pl-4 py-1`}>
                    <div className="flex flex-wrap items-center gap-3">
                      <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
                        {jobs.find(j => j.id === selectedJobId)?.title || t('candidates.header.selectedJob')}
                      </h1>
                      {selectedJobId && isSelectedJobCompleted && (
                        <span className="flex items-center gap-1 text-purple-600 bg-purple-50 px-2 py-1 rounded-full text-xs font-medium border border-purple-200 whitespace-nowrap">
                          <CheckCircle2 className="w-3 h-3" />
                          <span>{t('candidates.header.completed')}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="flex items-center gap-4">
                {selectedJobId && (
                  <>
                    <div className="flex items-center bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setViewMode('list')}
                        className={`p-2 rounded-md transition-colors ${
                          viewMode === 'list'
                            ? 'bg-white shadow-sm text-blue-600'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <List className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`p-2 rounded-md transition-colors ${
                          viewMode === 'grid'
                            ? 'bg-white shadow-sm text-blue-600'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <LayoutGrid className="w-5 h-5" />
                      </button>
                    </div>
                    {/* Show Add Candidate button for active jobs or See Final Report for completed jobs */}
                    {isSelectedJobCompleted ? (
                      <button
                        onClick={() => setShowFinalReport(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors focus:ring-4 focus:ring-green-500/50"
                      >
                        <FileText className="w-4 h-4" />
                        <span>{t('candidates.header.seeFinalReport')}</span>
                      </button>
                    ) : (
                      <>
                        <AddCandidateButton onClick={() => setShowAddModal(true)} />
                        <CompleteJobButton 
                          jobId={selectedJobId} 
                          onCompleted={() => {
                            // Mark the job as completed immediately in local state
                            const updatedJobs = jobs.map(j => 
                              j.id === selectedJobId ? {...j, status: 'COMPLETED'} : j
                            );
                            setJobs(updatedJobs);
                            
                            // Switch to COMPLETED view to show the completed job
                            setJobStatus('COMPLETED');
                            
                            // Update URL search params to reflect COMPLETED status
                            setSearchParams({
                              jobId: selectedJobId,
                              status: 'COMPLETED'
                            });
                            
                            // Trigger a refresh to get updated data from the server
                            setRefreshTrigger(prev => prev + 1);
                          }} 
                        />
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            {!selectedJobId ? (
              <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <FileText className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('candidates.noContent.noJobSelected.title')}</h3>
                <p className="text-gray-600 max-w-md mb-6">
                  {t('candidates.noContent.noJobSelected.subtitle')}
                </p>
              </div>
            ) : filteredCandidates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('candidates.noContent.noCandidates.title')}</h3>
                <p className="text-gray-600 max-w-md mb-6">
                  {t('candidates.noContent.noCandidates.subtitle')}
                </p>
              </div>
            ) : viewMode === 'list' ? (
              <div className="overflow-x-auto">
                <CandidatesTable 
                  selectedJobId={selectedJobId} 
                  candidatesData={candidates}
                  isJobCompleted={isSelectedJobCompleted}
                />
              </div>
            ) : (
              <CandidatesGrid
                selectedJobId={selectedJobId}
                candidates={filteredCandidates.map(adaptCandidateForDisplay)}
                onCandidateClick={handleCandidateClick}
                isJobCompleted={isSelectedJobCompleted}
              />
            )}
          </div>
        </main>
      </div>

      {showAddModal && (
        <AddCandidateModal
          jobs={jobs}
          preSelectedJobId={selectedJobId}
          onCandidateCreated={(newCandidate) => {
            // Add the new candidate to the local state immediately
            setCandidates(prev => [...prev, newCandidate]);
          }}
          onClose={() => {
            setShowAddModal(false);
            // Also trigger a background refresh for good measure
            setRefreshTrigger(prev => prev + 1);
          }}
        />
      )}

      {selectedCandidate && (
        <CandidateReferenceModal
          candidate={adaptCandidateForDisplay(selectedCandidate)}
          onClose={() => setSelectedCandidate(null)}
          isJobCompleted={isSelectedJobCompleted}
        />
      )}

      {showFinalReport && selectedJobId && (
        <FinalReportModal
          jobId={selectedJobId}
          jobTitle={jobs.find(j => j.id === selectedJobId)?.title || t('candidates.header.selectedJob')}
          onClose={() => setShowFinalReport(false)}
        />
      )}
    </div>
  );
};

export default Candidates;